import ProductListPageTypeConfig from "../ProductListPageTypeConfig";
import { Currency } from "../../../../js/common/CurrencyFormatter";
import elementService from "../../../../js/common/ElementService";
import productListFiltersModalService from "./ProductListFiltersModalService";
import ProductListFilter, { HashProductListFilterValues } from "./ProductListFilter";

type FilterOperations = Record<string, () => void>;

export class OnClearSearchQueryEvent extends Event {
    public static readonly Type = "clearsearchquery";

    constructor() {
        super(OnClearSearchQueryEvent.Type);
    }
}

export class OnClearFilterValueEvent extends Event {
    public static readonly Type = "clearfiltervalue";

    constructor() {
        super(OnClearFilterValueEvent.Type);
    }
}

export default class ProductListFilterContentContainer extends EventTarget {
    constructor(private readonly productListFilter: ProductListFilter) {
        super();
    }

    public initContent(pageTypeConfig: ProductListPageTypeConfig, currency: Currency): void {
        productListFiltersModalService.init(pageTypeConfig, currency, this.productListFilter);

        this.initClearFilterValueButtons();
    }

    public loadContentByHash(parsedHash: HashProductListFilterValues): void {
        this.productListFilter.fromJSONByUrlHash(parsedHash);
    }

    public setSearchQueryFilterValue(searchQuery: string): void {
        this.productListFilter.setSearchQueryValue(searchQuery);
    }

    public getCurrentFilter(): ProductListFilter {
        return this.productListFilter;
    }

    private initClearFilterValueButtons(): void {
        for (const clearFilterButton of document.querySelectorAll<HTMLAnchorElement>(".js-clear-product-filter")) {
            clearFilterButton.addEventListener("click", (event: MouseEvent) => {
                event.preventDefault();
                this.clearFilterValue(clearFilterButton);
            });
        }
    }

    private clearFilterValue(clearFilterButton: HTMLAnchorElement): void {
        const filterName = elementService.elementData(clearFilterButton, "filter");
        const filterValue = elementService.elementData(clearFilterButton, "filterValue");

        const clearFilterOperations: FilterOperations = {
            "search-query": (): void => {
                this.productListFilter.setSearchQueryValue("");
                this.dispatchEvent(new OnClearSearchQueryEvent());
            },
            "package-producer": () => this.productListFilter.removePackageProducer(Number.parseInt(filterValue)),
            "book-author": () => this.productListFilter.removeBookAuthor(Number.parseInt(filterValue)),
            "product-status": () => this.productListFilter.removeProductStatus(Number.parseInt(filterValue)),
            subcategory: () => this.productListFilter.removeSubcategory(Number.parseInt(filterValue)),
            "sell-tag": () => this.productListFilter.removeSellTag(Number.parseInt(filterValue)),
            parameter: (): void => this.productListFilter.removeParameter(Number.parseInt(filterValue), Number.parseInt(elementService.elementData(clearFilterButton, "filterParameterValue"))),
            "price-range": () => this.productListFilter.removePriceRangeValue(),
            "in-stock": () => this.productListFilter.removeInStock(Number.parseInt(filterValue)),
            all: () => this.clearAllFilters(),
        };

        if (!(filterName in clearFilterOperations)) {
            throw new Error(`Unsupported filter value: ${filterName}`);
        }

        clearFilterOperations[filterName]();

        this.productListFilter.setPage(1);

        this.dispatchEvent(new OnClearFilterValueEvent());
    }

    private clearAllFilters(): void {
        const searchQueryValue = this.productListFilter.getSearchQueryValue();
        this.productListFilter.removeAllFilters();

        if (searchQueryValue !== "") {
            this.dispatchEvent(new OnClearSearchQueryEvent());
        }
    }
}
